import { doc, getDoc, setDoc, serverTimestamp, arrayUnion, arrayRemove } from 'firebase/firestore';
import { User as FirebaseUser } from 'firebase/auth';
import { db } from '../firebase/FirebaseProvider';

// User roles
export enum UserRole {
  USER = 'USER',
  ADMIN = 'ADMIN',
  MODERATOR = 'MODERATOR'
}

export interface User {
  id: string;
  email: string;
  role: string;
  lastSeenAt: any; // Firestore timestamp
  createdAt?: any; // Firestore timestamp
  subscribedTopics?: string[]; // Array of topic IDs the user is subscribed to
}

const USERS_COLLECTION = 'users';

export class UserService {
  /**
   * Save or update a user in Firestore
   * If it's their first time logging in, create a new user document
   * Otherwise, just update the lastSeenAt timestamp
   */
  static async saveOrUpdateUser(firebaseUser: FirebaseUser): Promise<User> {
    const userRef = doc(db, USERS_COLLECTION, firebaseUser.uid);
    
    try {
      // Check if user already exists
      const userDoc = await getDoc(userRef);
      
      const userData: Partial<User> = {
        id: firebaseUser.uid,
        email: firebaseUser.email!,
        lastSeenAt: serverTimestamp()
      };

      if (!userDoc.exists()) {
        // First time user - create new document with default role
        userData.createdAt = serverTimestamp();
        // Set default role based on auth ID (only for new users)
        userData.role = firebaseUser.uid === 'QfgpRkHdTsUWIVsLeyX19SIywSE3' ? UserRole.ADMIN : UserRole.USER;
      }
      // For existing users, we don't touch the role at all - it can only be changed manually

      // Use merge: true to update existing fields or create new document
      await setDoc(userRef, userData, { merge: true });

      // For the return, we need to get the actual role from Firestore for existing users
      let actualRole = userData.role; // This will be set only for new users
      
      if (userDoc.exists() && !userData.role) {
        // For existing users, get the current role from Firestore
        const updatedDoc = await getDoc(userRef);
        if (updatedDoc.exists()) {
          actualRole = updatedDoc.data()?.role || UserRole.USER;
        }
      }

      // Return the user data (note: timestamps will be null in this return)
      return {
        id: firebaseUser.uid,
        email: firebaseUser.email!,
        role: actualRole || UserRole.USER,
        lastSeenAt: userData.lastSeenAt,
        ...(userData.createdAt && { createdAt: userData.createdAt })
      };
    } catch (error) {
      console.error('Error saving/updating user:', error);
      throw error;
    }
  }

  /**
   * Get a user by their ID
   */
  static async getUser(userId: string): Promise<User | null> {
    try {
      const userRef = doc(db, USERS_COLLECTION, userId);
      const userDoc = await getDoc(userRef);
      
      if (userDoc.exists()) {
        const userData = userDoc.data();
        return {
          id: userDoc.id,
          ...userData
        } as User;
      }
      
      return null;
    } catch (error) {
      console.error('Error getting user:', error);
      throw error;
    }
  }

  /**
   * Update a user's role
   */
  static async updateUserRole(userId: string, role: UserRole): Promise<void> {
    try {
      const userRef = doc(db, USERS_COLLECTION, userId);
      
      await setDoc(userRef, { role }, { merge: true });
      
      // Verify the update by reading back the document
      const updatedDoc = await getDoc(userRef);
      if (updatedDoc.exists()) {
        // Document verified successfully
      }
    } catch (error) {
      console.error('UserService - Error updating user role:', error);
      throw error;
    }
  }

  /**
   * Check if a user has a specific role
   */
  static hasRole(user: User | null, role: UserRole): boolean {
    return user?.role === role;
  }

  /**
   * Check if a user is an admin
   */
  static isAdmin(user: User | null): boolean {
    return UserService.hasRole(user, UserRole.ADMIN);
  }

  /**
   * Check if a user is a moderator or admin
   */
  static isModerator(user: User | null): boolean {
    return UserService.hasRole(user, UserRole.MODERATOR) || UserService.isAdmin(user);
  }

  /**
   * Subscribe a user to a topic
   */
  static async subscribeToTopic(userId: string, topicId: string): Promise<void> {
    try {
      const userRef = doc(db, USERS_COLLECTION, userId);
      await setDoc(userRef, { 
        subscribedTopics: arrayUnion(topicId) 
      }, { merge: true });
    } catch (error) {
      console.error('Error subscribing to topic:', error);
      throw error;
    }
  }

  /**
   * Unsubscribe a user from a topic
   */
  static async unsubscribeFromTopic(userId: string, topicId: string): Promise<void> {
    try {
      const userRef = doc(db, USERS_COLLECTION, userId);
      await setDoc(userRef, { 
        subscribedTopics: arrayRemove(topicId) 
      }, { merge: true });
    } catch (error) {
      console.error('Error unsubscribing from topic:', error);
      throw error;
    }
  }

  /**
   * Get user's subscribed topics
   */
  static async getUserSubscribedTopics(userId: string): Promise<string[]> {
    try {
      const user = await UserService.getUser(userId);
      return user?.subscribedTopics || [];
    } catch (error) {
      console.error('Error getting user subscribed topics:', error);
      throw error;
    }
  }
}